import React, {useCallback, useEffect, useReducer, useState} from 'react';
import {Liveticker} from 'app/components/match/matchLive/Liveticker';
import {Content} from 'app/styles/content';
import styled from 'styled-components';
import {ELEVATIONS, SIZES, LOADING_MESSAGE, Placeholder} from '@fupa/fupa-uikit';
import {Subheading} from 'app/styles/heading';
import {useDispatch, useSelector} from 'react-redux';
import * as Sentry from '@sentry/react';
import {
  deleteTickerLike,
  fetchMatchTicker,
  fetchMatchVideos,
  overwriteTicker,
  postTickerLike,
  websocketAddMatchVideos,
  websocketUpdateMatchVideos,
  wsDeleteHighlights,
  wsDeleteTicker,
  wsUpdateHighlights,
  wsUpdateTicker,
} from 'app/routes/match/MatchPageActions';
import {selectSsrRendered, selectAuthState, selectWebsocketData} from 'app/hooks/reduxCreateSelectorHooks';
import {AdSlot} from 'app/components/ads/AdSlot';
import {useLocation, useParams} from 'react-router';
import debounce from 'app/helpers/debounce';
import {useFetchData, useRestoreData, useEffectAfterMount, useDataFetchFinished} from 'app/hooks/dataHooks';
import {MatchTickerPageReducer} from 'app/routes/match/MatchPageReducer';
import {getPermission} from 'app/services/api';
import {VideoTeaser} from 'app/components/tv/preview/VideoTeaser';
import {
  getTickerLikeFromSessionStorage,
  removeTickerLikeFromSessionStorage,
  addTickerLikeToSessionStorage,
} from 'app/helpers/permissionsHelpers';
import {MatchEventTyping, useIsTyping} from 'app/components/match/matchLive/MatchEventTyping';
import LivetickerAssignment from 'app/components/match/matchLive/LivetickerAssignment';
import LivetickerLoadingIndicator from 'app/components/match/matchLive/LivetickerLoadingIndicator';
import {MatchTickerVotingButton, MatchTickerVotingOverlayAnimation} from 'app/components/match/MatchTickerVoting';
import LivetickerAuthor from 'app/components/match/LivetickerAuthor';
import SignUpTeaserDialog from 'app/components/auth/SignUpTeaserDialog';
import {updateAuthDialog} from 'app/actions/sessionActions';
import useShowAddScorerButton from 'app/components/match/liveResult/updateScorer/useShowAddScorerButton';
import getHighlightDataFromMatchEvent from 'app/components/match/liveResult/getHighlightDataFromMatchEvent';
import {SoftTickerContext, SoftTickerDispatchContext} from 'app/contexts/SoftTickerContext';
import softTickerReducer, {initialSoftTickerData} from 'app/reducers/softTickerReducer';
import SoftTickerDialog from 'app/components/match/liveResult/SoftTickerDialog';
import AddScorerButton from 'app/components/match/liveResult/updateScorer/AddScorerButton';
import {useNative} from 'app/contexts/NativeContext';
import {AuthDialogReducer, initialAuthDialogData} from 'app/reducers/authDialogReducer';
import {closeAuthDialog, updateAuthDialogData} from 'app/actions/authDialogActions';
import is from '@sindresorhus/is';
import SponsorAdSlotWithPresentedByClaim from 'app/components/ads/SponsorAdSlotWithPresentedByClaim';
import NativeRefreshButton from 'app/components/match/matchLive/NativeRefreshButton';
import {useLayout} from 'app/contexts/LayoutContext';
import {LayoutMode} from 'app/styles/LayoutMode';

const Container = styled(Content)`
  padding-bottom: 3.625rem;
  box-shadow: ${ELEVATIONS.Card};
  ${props => props.theme.desktopLayout`
    padding-bottom: 0;
  `}
`;

const Headline = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 3rem;
`;

const SCSubheading = styled(Subheading)`
  &&& {
    padding-top: 0;
    margin-bottom: 0;
  }
`;

const Authors = styled.div`
  position: relative;
  width: 100%;
  padding: 0 ${SIZES['16']} ${SIZES['8']};
}
`;

const AuthorContainer = styled.div`
  padding: ${SIZES['4']} 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const indexRect1 = 12;

const MatchTickerPage = ({matchInfo, loadDataMatchMeta, preventHistoryUpdate, onMatchChange}) => {
  const {restoredData} = useRestoreData('MatchTickerPage');
  const {matchSlug} = useParams();
  const isSSR = useSelector(selectSsrRendered);
  const websocketData = useSelector(selectWebsocketData);
  const initData = {
    isFetching: !!isSSR,
    items: null,
    error: null,
    isFetchingVideos: false,
    errorVideos: null,
    videos: [],
  };
  const initialState = restoredData ?? initData;
  const [state, dispatch] = useReducer(MatchTickerPageReducer, initialState);
  const [tickerLiked, setTickerLiked] = useState(() => getTickerLikeFromSessionStorage(matchSlug));
  const [checked, setChecked] = useState(false);
  const {isNativeApp} = useNative();
  const layout = useLayout();
  const isDesktop = layout !== LayoutMode.Mobile;
  const reduxDispatch = useDispatch();
  const [softTickerData, dispatchSoftTicker] = useReducer(softTickerReducer, initialSoftTickerData);
  const [authDialogData, dispatchAuthDialogData] = useReducer(AuthDialogReducer, initialAuthDialogData);

  const loadData = (reloading, forceRefresh, ts) => {
    const tsParam = ts ? ts : matchInfo?.streamUpdatedAt;
    fetchMatchTicker(matchSlug, tsParam, reloading, forceRefresh, isDesktop)(dispatch, state);
  };

  const loadMatchVideos = forceRefresh =>
    fetchMatchVideos(matchSlug, matchInfo?.updatedAt, forceRefresh)(dispatch, state);

  const {items, isFetching, error, videos, errorVideos} = state;
  const tickerEvents = items ?? [];
  const isFetchingTickerEvents = isFetching;
  // Look out: loadData will be fetched further down on matchInfo-Change,
  // this is important for the unmount-function in useFetchData
  useFetchData(state, () => {}, 'MatchTickerPage', false, true, preventHistoryUpdate);
  const location = useLocation();
  const isAuthenticated = useSelector(selectAuthState);
  const [permissions, setPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  useEffectAfterMount(() => {
    switch (websocketData?.event) {
      case 'liveticker.created':
        dispatch(wsUpdateTicker({items: [websocketData.data]}));
        break;
      case 'liveticker.updated':
        dispatch(wsUpdateTicker(websocketData.data, true));
        break;
      case 'liveticker.deleted':
        dispatch(wsDeleteTicker(websocketData.data));
        break;
      case 'video.created':
        dispatch(websocketUpdateMatchVideos(websocketData.data));
        break;
      case 'video_element.created':
        dispatch(websocketAddMatchVideos(websocketData.data));
        break;
    }
  }, [websocketData]);

  // fetch stream- and video-data as soon as match-info is available, to add updatedAt
  useEffect(() => {
    if (Object.keys(matchInfo).length) {
      loadData(false, isSSR);
    }
  }, [matchInfo?.slug]);

  useEffect(() => {
    if (matchInfo?.flags?.includes('fupatv')) {
      loadMatchVideos();
    }
  }, [matchSlug, matchInfo.updatedAt]);

  // clear data on switching match by navigation to firstMatch
  useEffectAfterMount(() => {
    dispatch(overwriteTicker(initData));
  }, [location.pathname]);

  useEffect(() => {
    let timer;
    if (checked) {
      timer = setTimeout(() => {
        setChecked(() => false);
      }, 1500);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [checked]);

  useEffect(() => {
    const permissionIsSet = is.boolean(permissions.alreadyLiked);
    if (permissionIsSet) {
      setTickerLiked(permissions.alreadyLiked);
    }
  }, [permissions]);

  const onDialogClose = () => {
    dispatchAuthDialogData(closeAuthDialog());
  };

  const openAuthDialog = (title, subtitle, utmString) => {
    reduxDispatch(updateAuthDialog(true));
    dispatchAuthDialogData(updateAuthDialogData({open: true, title, subtitle, utmString}));
  };

  const handleTickerLike = () => {
    if (!isAuthenticated) {
      openAuthDialog(
        'Erstelle ein Konto, um für einen Top-Ticker zu stimmen und in Echtzeit mitzulesen.',
        'Mit deiner Reaktion bedankst du dich beim Liveticker-Melder für seinen Einsatz.',
        'signup_teaser_match_ticker'
      );
      return;
    }
    if (tickerLiked) {
      deleteTickerLike(matchSlug)
        .then(() => removeTickerLikeFromSessionStorage(matchSlug))
        .catch(() => {
          setTickerLiked(true);
        });
    } else {
      setChecked(true);
      postTickerLike(matchSlug)
        .then(() => addTickerLikeToSessionStorage(matchSlug))
        .catch(e => {
          Sentry.captureException(e);
          setTickerLiked(false);
        });
    }
    setTickerLiked(prev => !prev);
  };

  const askPermission = async () => {
    setLoadingPermissions(true);
    try {
      const response = await getPermission('match', matchSlug);
      setPermissions(response.data);
    } catch (e) {}
    setLoadingPermissions(false);
  };

  // initially ask for permission
  useEffect(() => {
    const permissionIsSet = is.boolean(permissions.alreadyLiked);

    const tickerAssignCondition =
      !permissionIsSet && isAuthenticated && matchInfo?.slug && matchInfo?.section !== 'POST';
    const tickerLikeableCondition = !permissionIsSet && isAuthenticated && matchInfo?.tickerLikeable;

    if (tickerAssignCondition || tickerLikeableCondition) {
      askPermission();
    } else if (!isAuthenticated && permissionIsSet) {
      // handle user sign-out on MatchTickerPage
      setPermissions({});
    }
  }, [matchInfo, isAuthenticated]);

  const onRefresh = useCallback(
    debounce(
      () => {
        reloadData();
        //Todo: check why permission is needed to be reset on reload
        setPermissions(prevState => ({...prevState, alreadyLiked: false}));
        askPermission();
      },
      500,
      true
    ),
    []
  );

  const loadingTickerFinished = useDataFetchFinished(isFetchingTickerEvents);

  const handleScorerUpdate = (matchEvent, isUpdate) => {
    const eventData = isUpdate ? matchEvent : {items: [matchEvent]};
    dispatch(wsUpdateTicker(eventData, isUpdate));
    onMatchChange(() => wsUpdateHighlights(getHighlightDataFromMatchEvent(matchEvent), isUpdate));
  };

  const handleScorerDelete = matchEventId => {
    dispatch(wsDeleteTicker({entity: {id: matchEventId}}));
    onMatchChange(() => wsDeleteHighlights({id: matchEventId}));
  };

  const reloadData = () => {
    const now = new Date().getTime();
    dispatch(overwriteTicker(initData));
    loadData(false, true, now);
    loadDataMatchMeta(true, now);
    loadMatchVideos(true);
    askPermission();
  };
  const isTypingCategory = useIsTyping();
  const displayTicker = (
    <Liveticker error={error} events={tickerEvents} isFetching={isFetchingTickerEvents} match={matchInfo} />
  );

  let displayAuthors, adUnit;
  if (Object.keys(matchInfo).length) {
    const nativeGuestUser = isNativeApp && !isAuthenticated;
    const showTickerVotingButton = matchInfo?.tickerLikeable && !nativeGuestUser;
    displayAuthors = matchInfo.tickerAuthor ? (
      <>
        <MatchTickerVotingOverlayAnimation animate={checked} active={tickerLiked} />
        <AuthorContainer>
          <LivetickerAuthor author={matchInfo.tickerAuthor}>
            {isTypingCategory !== null && <MatchEventTyping category={isTypingCategory} />}
          </LivetickerAuthor>
          {showTickerVotingButton && <MatchTickerVotingButton active={tickerLiked} handleClick={handleTickerLike} />}
        </AuthorContainer>
      </>
    ) : null;

    // place classic ad slot after all data has been fetched to avoid to re-define ad unit 'Rectangle_1'
    adUnit =
      loadingTickerFinished && displayTicker?.props.events.length < indexRect1 ? <AdSlot adUnit='Rectangle_1' /> : null;
  }

  const addScorerButton = useShowAddScorerButton(matchInfo, tickerEvents) ? <AddScorerButton /> : null;

  const videoTeaser = videos.length && !errorVideos ? <VideoTeaser videos={videos} /> : null;

  if (error) {
    return (
      <Container>
        <Placeholder>Beim Laden der Meldungen ist ein Fehler aufgetreten.</Placeholder>
      </Container>
    );
  }

  let optionalAuthorInfoOrScorerButton = null;
  if (displayAuthors) {
    optionalAuthorInfoOrScorerButton = <Authors>{displayAuthors}</Authors>;
  } else if (addScorerButton) {
    optionalAuthorInfoOrScorerButton = addScorerButton;
  }

  return !items ? (
    <Container>
      <LivetickerLoadingIndicator message={LOADING_MESSAGE.loadingStream} />
    </Container>
  ) : (
    <SoftTickerContext.Provider value={softTickerData}>
      <SoftTickerDispatchContext.Provider value={dispatchSoftTicker}>
        <Container noContentPadding>
          <LivetickerAssignment
            matchInfo={matchInfo}
            reloadData={reloadData}
            assignTickerAt={permissions.assignTickerAt}
            tickerFailure={permissions.tickerFailure}
            isLoading={loadingPermissions && !isFetchingTickerEvents}
            setPermissions={setPermissions}
            openAuthDialog={openAuthDialog}
          />
          {videoTeaser}
          <Headline>
            <SCSubheading variant='subheading2'>Aktuelle Meldungen</SCSubheading>
            <SponsorAdSlotWithPresentedByClaim extendSlotId={'liveticker'} />
            <NativeRefreshButton onClick={onRefresh} />
          </Headline>
          {optionalAuthorInfoOrScorerButton}
          <SignUpTeaserDialog {...authDialogData} handleClose={onDialogClose} />
          {displayTicker}
          {adUnit}
        </Container>
        <SoftTickerDialog
          match={matchInfo}
          tickerEvents={tickerEvents}
          onMatchChange={onMatchChange}
          onScorerDelete={handleScorerDelete}
          onScorerChange={handleScorerUpdate}
        />
      </SoftTickerDispatchContext.Provider>
    </SoftTickerContext.Provider>
  );
};

export default MatchTickerPage;
